import React, { useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import AltRetailerLogo from 'images/vr-new-retail_3.png'
import { MoreHorizontal, ChevronUp, ChevronDown, ArrowUpDown, Briefcase, Trash2 } from 'lucide-react'

import useTable from 'components/controls/useTable'
import Layout from 'components/Layout'
import Spinner from 'components/Spinner'
import Message from 'components/Message'
import Popup from 'components/Popup'
import {
	deletePortfolio,
	initializeArchivedPortfolios,
	initializePortfolios,
	updatePortfolio,
} from 'slices/portfolioSlice'
import PortfolioDeletePopup from 'components/popups/PortfolioDeletePopup'
import PortfolioEditPopup from 'components/popups/PortfolioEditPopup'
import {
	Table,
	TableHeader,
	TableBody,
	TableHead,
	TableRow,
	TableCell,
} from 'components/ui/table'
import { TablePagination } from 'components/ui/pagination'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from 'components/ui/dropdown-menu'

const headerCells = [
	{ id: 'Image', label: '', sortable: false },
	{ id: 'assetName', label: 'Portfolio' },
	{ id: 'description', label: 'Notes' },
	{ id: 'locations', label: 'Locations' },
	{ id: 'isActive', label: 'Status' },
	{ id: 'reLoad', label: 'Actions', sortable: false },
]

const options = ['Edit/Modify', 'Delete']

const PortfoliosList = () => {
	const dispatch = useDispatch()

	const searchInputRef = useRef(null)
	const [searchInputValue, setSearchInputValue] = useState('')
	const [openModifyPopup, setOpenModifyPopup] = useState(false)
	const [openArchivePopup, setOpenArchivePopup] = useState(false)
	const [openDeletePopup, setOpenDeletePopup] = useState(false)
	const [popupTitle, setPopupTitle] = useState('')
	const [recordForEdit, setRecordForEdit] = useState(null)

	const [orderBy, setOrderBy] = useState(null)
	const [order, setOrder] = useState('asc')
	const [page, setPage] = useState(0)
	const [rowsPerPage, setRowsPerPage] = useState(10)

	const [filterFn, setFilterFn] = useState({
		fn: items => {
			return items
		},
	})

	const userLogin = useSelector(state => state.userLogin)
	const { userInfo: user } = userLogin
	const userId = user._id

	const { entities: portfolios, error, ids: portfolioIds, isLoading, isInitialized } = useSelector(
		state => state.portfolios,
	)
	const records = portfolioIds.map(id => portfolios[id])

	useEffect(() => {
		dispatch(initializePortfolios())
	}, [dispatch, isInitialized])

	const recordCount = records.length > 0

	const { recordsAfterPagingAndSorting } = useTable(
		records,
		headerCells,
		filterFn,
		true
	)

	const handleSort = columnId => {
		if (orderBy === columnId) {
			setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setOrderBy(columnId)
			setOrder('asc')
		}
	}

	const getNestedValue = (obj, path) => {
		return path.split('.').reduce((acc, key) => acc?.[key], obj)
	}

	const filteredRecords = recordsAfterPagingAndSorting()

	const sortedRecords = useMemo(() => {
		if (!orderBy) return filteredRecords
		return [...filteredRecords].sort((a, b) => {
			const aVal = (getNestedValue(a, orderBy) || '').toString().toLowerCase()
			const bVal = (getNestedValue(b, orderBy) || '').toString().toLowerCase()
			if (aVal < bVal) return order === 'asc' ? -1 : 1
			if (aVal > bVal) return order === 'asc' ? 1 : -1
			return 0
		})
	}, [filteredRecords, orderBy, order])

	const paginatedRecords = useMemo(() => {
		const start = page * rowsPerPage
		return sortedRecords.slice(start, start + rowsPerPage)
	}, [sortedRecords, page, rowsPerPage])

	const SortIcon = ({ columnId }) => {
		if (orderBy !== columnId) return <ArrowUpDown className="ml-1 h-3 w-3 text-gray-300" />
		return order === 'asc' ? (
			<ChevronUp className="ml-1 h-3 w-3 text-primary" />
		) : (
			<ChevronDown className="ml-1 h-3 w-3 text-primary" />
		)
	}

	const handleSearch = e => {
		const { value } = e.target
		setSearchInputValue(value)
		setFilterFn({
			fn: items => {
				if (value === '') return items
				const lowerValue = value.toLowerCase()
				return items.filter(
					x =>
						(x.assetName && x.assetName.toLowerCase().includes(lowerValue)) ||
						(x.description && x.description.toLowerCase().includes(lowerValue)),
				)
			},
		})
		// set focus to search input after rerender
		setTimeout(() => {
			searchInputRef.current.focus()
		}, 0)
	}

	const hasRows = records ? records.length : null

	const handleEditPortfolio = async newPortfolio => {
		const portfolioId = newPortfolio._id

		newPortfolio.updateNote = 'Edited Portfolio.'
		await dispatch(updatePortfolio(newPortfolio))

		dispatch(
			initializeArchivedPortfolios({
				portfolioId: portfolioId,
			}),
		)

		setOpenModifyPopup(false)
	}

	const handleDeletePortfolio = async newPortfolio => {
		const portfolioId = newPortfolio._id

		await dispatch(deletePortfolio({ portfolioId }))

		dispatch(
			initializeArchivedPortfolios({
				portfolioId: portfolioId,
			}),
		)

		setOpenDeletePopup(false)
	}

	return (
		<div className="space-y-2">
			<Layout>
				<Layout.Page>
					<Layout.Body>
						{isLoading ? (
							<Spinner containerHeight="50vh" />
						) : !records ? (
							<Message variant="danger">{error}</Message>
						) : error ? (
							<Message variant="danger">{error}</Message>
						) : recordCount ? (
							<>
								<div>
									<div className="grid grid-cols-12 gap-6">
										<div className="col-span-12">
											<Layout.PageToolbarNoPlus
												searchInputRef={searchInputRef}
												searchInputValue={searchInputValue}
												searchInputOnChange={handleSearch}
											/>

											<Table>
												<TableHeader>
													<TableRow className="border-b-2 border-[#027fff] hover:bg-transparent">
														{headerCells.map(cell => (
															<TableHead
																key={cell.id}
																className={
																	cell.sortable !== false
																		? 'cursor-pointer select-none text-gray-700 text-xs font-semibold whitespace-nowrap'
																		: 'text-gray-700 text-xs font-semibold whitespace-nowrap'
																}
																onClick={cell.sortable !== false ? () => handleSort(cell.id) : undefined}
															>
																<span className="inline-flex items-center">
																	{cell.label}
																	{cell.sortable !== false && <SortIcon columnId={cell.id} />}
																</span>
															</TableHead>
														))}
													</TableRow>
												</TableHeader>
												<TableBody>
													{paginatedRecords.map(portfolio => (
														<TableRow key={portfolio.assetName} className="h-7 hover:bg-[#fffbf2] cursor-pointer">
															<TableCell className="py-1 text-sm">
																<Avatar>
																	<AvatarFallback className="text-xs bg-gray-200 text-gray-500">{portfolio.assetName?.charAt(0) || 'P'}</AvatarFallback>
																	<AvatarFallback />
																</Avatar>
															</TableCell>

															<TableCell className="py-1 text-sm">{portfolio.assetName}</TableCell>
															<TableCell className="py-1 text-sm">{portfolio.description}</TableCell>
															<TableCell className="py-1 text-sm">{portfolio.locations.length}</TableCell>
															<TableCell className="py-1 text-sm">{portfolio.isActive ? 'Active' : 'In Active'}</TableCell>
															<TableCell className="py-1 text-sm">
																<MenuButton
																	portfolio={portfolio}
																	setPopupTitle={setPopupTitle}
																	setRecordForEdit={setRecordForEdit}
																	setOpenDeletePopup={setOpenDeletePopup}
																	setOpenModifyPopup={setOpenModifyPopup}
																/>
															</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
											{hasRows > 4 ? (
												<TablePagination
													page={page}
													rowsPerPage={rowsPerPage}
													count={sortedRecords.length}
													onPageChange={setPage}
													onRowsPerPageChange={setRowsPerPage}
												/>
											) : null}
										</div>
									</div>
								</div>
							</>
						) : null}
					</Layout.Body>
				</Layout.Page>
			</Layout>

			{openModifyPopup ? (
				<Popup title={popupTitle} openPopup={openModifyPopup} setOpenPopup={setOpenModifyPopup} icon={<Briefcase className="w-5 h-5" />} description="Edit portfolio details">
					<PortfolioEditPopup editPortfolio={handleEditPortfolio} recordForEdit={recordForEdit} />
				</Popup>
			) : null}

			{openDeletePopup ? (
				<Popup title={popupTitle} openPopup={openDeletePopup} setOpenPopup={setOpenDeletePopup} icon={<Trash2 className="w-5 h-5" />} description="Permanently delete this portfolio">
					<PortfolioDeletePopup deletePortfolio={handleDeletePortfolio} portfolio={recordForEdit} />
				</Popup>
			) : null}
		</div>
	)
}

const MenuButton = ({
	portfolio,
	setPopupTitle,
	setRecordForEdit,
	setOpenArchivePopup,
	setOpenDeletePopup,
	setOpenModifyPopup,
}) => {
	const handleAction = (option, portfolio) => {
		switch (option) {
			case 'Delete':
				setPopupTitle(`Delete Portfolio: ${portfolio.assetName}`)
				setRecordForEdit(portfolio)
				setOpenDeletePopup(true)
				break

			case 'Edit/Modify':
				setPopupTitle(`Edit/Modify Portfolio: ${portfolio.assetName}`)
				setRecordForEdit(portfolio)
				setOpenModifyPopup(true)
				break

			default:
				break
		}
	}

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button
					className="inline-flex items-center justify-center rounded-full p-1 text-[#0080fe] hover:bg-gray-100 transition-colors"
					aria-label="more"
				>
					<MoreHorizontal className="h-5 w-5" />
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-[15ch]">
				{options.map(option => (
					<DropdownMenuItem
						key={option}
						onClick={() => handleAction(option, portfolio)}
						className="text-[#0080fe] cursor-pointer"
					>
						{option}
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}

export default PortfoliosList
